/* Halo theme — vanilla JS, no dependencies. */
(() => {
  'use strict';

  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const T = window.theme || {};
  const routes = T.routes || {};
  const strings = T.strings || {};
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);

  /* ---------- Money ---------- */
  function formatMoney(cents, format) {
    const template = (format || T.moneyFormat || '${{amount}}').replace(/<[^>]*>/g, '');
    const value = Number(cents) || 0;
    const group = (amount, decimals, thousands = ',', decimal = '.') => {
      const [whole, fraction] = (amount / 100).toFixed(decimals).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
    };
    return template.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return group(value, 0);
        case 'amount_with_comma_separator': return group(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return group(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return group(value, 2, "'", '.');
        case 'amount_with_space_separator': return group(value, 2, ' ', ',');
        default: return group(value, 2);
      }
    });
  }

  /* ---------- Toast ---------- */
  let toastTimer;
  function toast(message) {
    const el = $('[data-toast]');
    if (!el || !message) return;
    el.textContent = message;
    el.hidden = false;
    requestAnimationFrame(() => el.classList.add('is-visible'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      el.classList.remove('is-visible');
      setTimeout(() => { el.hidden = true; }, 400);
    }, 3200);
  }

  /* ---------- Overlays (cart drawer + mobile menu) ---------- */
  let lastFocus = null;
  function openOverlay(el, focusTarget) {
    if (!el) return false;
    lastFocus = document.activeElement;
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('is-locked');
    setTimeout(() => (focusTarget || el).focus({ preventScroll: true }), 60);
    return true;
  }
  function closeOverlay(el) {
    if (!el || !el.classList.contains('is-open')) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    if (!$('.drawer.is-open, .mobile-menu.is-open')) document.documentElement.classList.remove('is-locked');
    if (lastFocus && document.contains(lastFocus)) lastFocus.focus({ preventScroll: true });
  }
  const drawerEl = () => $('[data-cart-drawer]');
  const openDrawer = () => openOverlay(drawerEl(), $('[data-cart-drawer] .drawer__panel'));
  const closeDrawer = () => closeOverlay(drawerEl());
  const menuEl = () => $('[data-mobile-menu]');
  function setMenuExpanded(value) {
    $$('[data-menu-open]').forEach((btn) => btn.setAttribute('aria-expanded', String(value)));
  }

  /* ---------- Cart API ---------- */
  function updateCount(count) {
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.classList.toggle('is-empty', Number(count) === 0);
      el.classList.remove('bump');
      void el.offsetWidth;
      el.classList.add('bump');
    });
  }

  const Cart = {
    sectionIds() {
      const ids = [];
      if (drawerEl()) ids.push('cart-drawer');
      const main = $('[data-main-cart]');
      if (main) ids.push(main.dataset.sectionId);
      return ids;
    },
    async post(url, body) {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: this.sectionIds(), sections_url: window.location.pathname })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.description || data.message || 'Something went wrong. Please try again.');
      return data;
    },
    async add(items) {
      const data = await this.post(`${routes.cartAdd}.js`, { items });
      this.render(data.sections);
      return data;
    },
    async change(key, quantity) {
      const data = await this.post(`${routes.cartChange}.js`, { id: key, quantity });
      this.render(data.sections);
      if (typeof data.item_count === 'number') updateCount(data.item_count);
      return data;
    },
    render(sections) {
      if (!sections) return;
      const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
      if (sections['cart-drawer']) {
        const fresh = $('[data-cart-drawer-inner]', parse(sections['cart-drawer']));
        const current = $('[data-cart-drawer-inner]');
        if (fresh && current) {
          current.replaceWith(fresh);
          updateCount(fresh.dataset.count);
        }
      }
      const main = $('[data-main-cart]');
      if (main && sections[main.dataset.sectionId]) {
        const fresh = $('[data-main-cart]', parse(sections[main.dataset.sectionId]));
        if (fresh) main.innerHTML = fresh.innerHTML;
      }
    }
  };

  function afterAdd() {
    if (T.cartType === 'drawer' && drawerEl()) openDrawer();
    else window.location.href = routes.cart;
  }

  async function changeLine(item, quantity) {
    const key = item.dataset.key;
    item.classList.add('is-loading');
    try {
      await Cart.change(key, Math.max(0, quantity));
    } catch (error) {
      item.classList.remove('is-loading');
      toast(error.message);
    }
  }

  /* ---------- Global click handling ---------- */
  document.addEventListener('click', async (event) => {
    const target = event.target;

    if (target.closest('[data-cart-toggle]') && T.cartType === 'drawer' && drawerEl() && !document.body.classList.contains('template-cart')) {
      event.preventDefault();
      openDrawer();
      return;
    }
    if (target.closest('[data-cart-close]')) { event.preventDefault(); closeDrawer(); return; }

    if (target.closest('[data-menu-open]')) { openOverlay(menuEl(), $('[data-mobile-menu] .mobile-menu__panel')); setMenuExpanded(true); return; }
    if (target.closest('[data-menu-close]') || target.closest('[data-menu-link]')) { closeOverlay(menuEl()); setMenuExpanded(false); return; }

    const quickAdd = target.closest('[data-quick-add]');
    if (quickAdd) {
      event.preventDefault();
      quickAdd.classList.add('is-loading');
      try {
        await Cart.add([{ id: Number(quickAdd.dataset.quickAdd), quantity: 1 }]);
        afterAdd();
      } catch (error) {
        toast(error.message);
      } finally {
        quickAdd.classList.remove('is-loading');
      }
      return;
    }

    const lineButton = target.closest('[data-line-quantity]');
    if (lineButton) {
      const item = lineButton.closest('[data-key]');
      if (item) changeLine(item, Number(lineButton.dataset.lineQuantity));
      return;
    }

    const step = target.closest('[data-qty-step]');
    if (step) {
      const input = $('input', step.parentElement);
      input.value = Math.max(Number(input.min) || 1, (Number(input.value) || 1) + Number(step.dataset.qtyStep));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-line-input]');
    if (input) {
      const item = input.closest('[data-key]');
      if (item) changeLine(item, Number(input.value) || 0);
    }
    if (event.target.matches('[data-auto-submit]')) event.target.form.submit();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    closeDrawer();
    closeOverlay(menuEl());
    setMenuExpanded(false);
  });

  /* ---------- Product form (add to cart without leaving the page) ---------- */
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form.product-form');
    if (!form || T.cartType !== 'drawer' || !drawerEl()) return;
    event.preventDefault();

    const button = $('[type="submit"]', form);
    const error = $('[data-form-error]', form);
    const data = new FormData(form);
    const item = { id: Number(data.get('id')), quantity: Math.max(1, Number(data.get('quantity')) || 1) };
    if (data.get('selling_plan')) item.selling_plan = data.get('selling_plan');
    const properties = {};
    for (const [key, value] of data.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && value !== '') properties[match[1]] = value;
    }
    if (Object.keys(properties).length) item.properties = properties;

    button.classList.add('is-loading');
    button.setAttribute('aria-disabled', 'true');
    if (error) error.hidden = true;
    try {
      await Cart.add([item]);
      afterAdd();
    } catch (err) {
      if (error) { error.textContent = err.message; error.hidden = false; } else toast(err.message);
    } finally {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-disabled');
    }
  });

  /* ---------- Product page ---------- */
  function initGallery(root) {
    const gallery = $('[data-gallery]', root);
    if (!gallery) return null;
    const track = $('[data-gallery-track]', gallery);
    const slides = $$('.gallery__slide', gallery);
    const thumbs = $$('[data-gallery-thumb]', gallery);
    const dots = $$('.gallery__dots span', gallery);
    let index = 0;

    const setActive = (i) => {
      index = i;
      thumbs.forEach((thumb, n) => thumb.classList.toggle('is-active', n === i));
      dots.forEach((dot, n) => dot.classList.toggle('is-active', n === i));
    };
    const go = (i) => {
      const next = Math.max(0, Math.min(slides.length - 1, i));
      track.scrollTo({ left: slides[next].offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
      setActive(next);
    };

    let ticking = false;
    track.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
        if (i !== index) setActive(i);
        ticking = false;
      });
    }, { passive: true });

    thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => go(i)));
    const prev = $('[data-gallery-prev]', gallery);
    const next = $('[data-gallery-next]', gallery);
    if (prev) prev.addEventListener('click', () => go(index - 1));
    if (next) next.addEventListener('click', () => go(index + 1));

    return {
      goToMedia(id) {
        const i = slides.findIndex((slide) => slide.dataset.mediaId === String(id));
        if (i > -1) go(i);
      }
    };
  }

  function initProduct(root) {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';

    const json = $('[data-variant-json]', root);
    const variants = json ? JSON.parse(json.textContent) : [];
    const form = $('form.product-form', root);
    const gallery = initGallery(root);
    if (!form) return;

    const idInput = $('[data-variant-id]', form);
    const qtyInput = $('[data-quantity-input]', form);
    const submit = $('[data-add-to-cart]', form);
    const atcLabel = $('[data-atc-label]', form);
    const atcPrice = $('[data-atc-price]', form);
    const sticky = $('[data-sticky-atc]', root);
    let current = variants.find((v) => v.id === Number(idInput.value)) || variants[0];

    const selectedDiscount = () => {
      const tier = $('[data-qb-input]:checked', root);
      return tier ? Number(tier.dataset.discount) || 0 : 0;
    };

    function renderPrices() {
      if (!current) return;
      const qty = Math.max(1, Number(qtyInput && qtyInput.value) || 1);
      const total = Math.round(current.price * qty * (100 - selectedDiscount()) / 100);
      if (atcPrice) atcPrice.textContent = formatMoney(total);
      if (sticky) {
        const stickyPrice = $('[data-sticky-price]', sticky);
        if (stickyPrice) stickyPrice.textContent = formatMoney(total);
      }
      $$('[data-qb-input]', root).forEach((input) => {
        const tier = input.closest('.qb__tier');
        const q = Number(input.value);
        const unitBase = Math.max(current.compare_at_price || 0, current.price);
        const price = Math.round(current.price * q * (100 - (Number(input.dataset.discount) || 0)) / 100);
        const priceEl = $('[data-qb-price]', tier);
        const compareEl = $('[data-qb-compare]', tier);
        if (priceEl) priceEl.textContent = formatMoney(price);
        if (compareEl) {
          compareEl.textContent = formatMoney(unitBase * q);
          compareEl.hidden = unitBase * q <= price;
        }
      });
    }

    function renderMainPrice(variant) {
      const box = $('[data-price]', root);
      if (!box) return;
      const onSale = variant.compare_at_price > variant.price;
      $('[data-price-current]', box).textContent = formatMoney(variant.price);
      const compare = $('[data-price-compare]', box);
      const badge = $('[data-price-badge]', box);
      compare.hidden = !onSale;
      badge.hidden = !onSale;
      box.classList.toggle('price--sale', onSale);
      if (onSale) {
        compare.textContent = formatMoney(variant.compare_at_price);
        const pct = Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price);
        badge.textContent = (strings.savePercent || 'Save [percent]%').replace('[percent]', pct);
      }
    }

    function renderStock(variant) {
      const stock = $('[data-stock]', root);
      if (!stock) return;
      const text = $('[data-stock-text]', stock);
      const threshold = Number(stock.dataset.threshold) || 0;
      let state = 'in';
      let label = stock.dataset.inText;
      if (!variant.available) {
        state = 'out';
        label = strings.soldOut;
      } else if (variant.inventory !== null && variant.inventory > 0 && variant.inventory <= threshold) {
        state = 'low';
        label = (strings.lowStock || 'Only [count] left').replace('[count]', variant.inventory);
      }
      stock.className = `stock stock--${state}`;
      text.textContent = label;
    }

    function setVariant(variant) {
      current = variant;
      const stickyButton = sticky && $('[data-sticky-submit]', sticky);
      const stickyLabel = sticky && $('[data-sticky-label]', sticky);
      if (!variant) {
        submit.disabled = true;
        atcLabel.textContent = strings.unavailable;
        if (stickyButton) stickyButton.disabled = true;
        if (stickyLabel) stickyLabel.textContent = strings.unavailable;
        return;
      }
      idInput.value = variant.id;
      $$('[data-installments] input[name="id"]', root).forEach((input) => {
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      submit.disabled = !variant.available;
      atcLabel.textContent = variant.available ? strings.addToCart : strings.soldOut;
      if (stickyButton) stickyButton.disabled = !variant.available;
      if (stickyLabel) stickyLabel.textContent = atcLabel.textContent;
      renderMainPrice(variant);
      renderStock(variant);
      renderPrices();
      if (variant.media_id && gallery) gallery.goToMedia(variant.media_id);
      if (!designMode) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    root.addEventListener('change', (event) => {
      const input = event.target;
      if (input.matches('[data-option-index]')) {
        const fieldset = input.closest('fieldset');
        const label = $('[data-option-label]', fieldset);
        if (label) label.textContent = input.value;
        const selected = $$('[data-variant-picker] fieldset', root).map((fs) => {
          const checked = $('input:checked', fs);
          return checked ? checked.value : null;
        });
        setVariant(variants.find((v) => v.options.every((option, i) => option === selected[i])));
      }
      if (input.matches('[data-qb-input]')) {
        if (qtyInput) qtyInput.value = input.value;
        $$('.qb__tier', root).forEach((tier) => tier.classList.toggle('is-selected', tier.contains(input)));
        renderPrices();
      }
      if (input === qtyInput) renderPrices();
    });

    const initialTier = $('[data-qb-input]:checked', root);
    if (initialTier && qtyInput) qtyInput.value = initialTier.value;
    renderPrices();

    if (current && current.media_id && gallery && new URL(window.location.href).searchParams.has('variant')) {
      gallery.goToMedia(current.media_id);
    }

    if (sticky && submit && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.classList.toggle('is-visible', show);
        sticky.setAttribute('aria-hidden', String(!show));
      });
      observer.observe(submit);
      $('[data-sticky-submit]', sticky).addEventListener('click', () => {
        if (submit.disabled) return;
        if (form.requestSubmit) form.requestSubmit(submit);
        else submit.click();
      });
    }
  }

  /* ---------- Sale countdown ---------- */
  // Per-visitor mode: one countdown per visitor, kept across pages and reloads, never restarted.
  // Fixed mode: everyone counts down to the same end date. Timers hide themselves at zero.
  const countdownEnds = {};
  function countdownEnd(el) {
    if (el.dataset.mode === 'date') {
      const time = Date.parse((el.dataset.end || '').trim().replace(' ', 'T'));
      return Number.isNaN(time) ? null : time;
    }
    const minutes = Number(el.dataset.minutes) || 25;
    const key = `halo:countdown:${minutes}`;
    if (countdownEnds[key]) return countdownEnds[key];
    let end = null;
    if (!designMode) {
      try { end = Number(localStorage.getItem(key)) || null; } catch (error) { /* storage unavailable */ }
    }
    if (!end) {
      end = Date.now() + minutes * 60000;
      if (!designMode) {
        try { localStorage.setItem(key, String(end)); } catch (error) { /* storage unavailable */ }
      }
    }
    countdownEnds[key] = end;
    return end;
  }
  function setCountdownUnit(el, unit, value, visible) {
    const node = $(`[data-cd="${unit}"]`, el);
    if (node && node.textContent !== String(value)) node.textContent = value;
    $$(`[data-unit="${unit}"]`, el).forEach((part) => { part.hidden = !visible; });
  }
  function tickCountdowns() {
    const now = Date.now();
    $$('[data-countdown]').forEach((el) => {
      if (!el.dataset.endAt) {
        const end = countdownEnd(el);
        el.dataset.endAt = end ? String(end) : 'none';
      }
      const wrap = el.closest('[data-countdown-wrap]') || el;
      const left = el.dataset.endAt === 'none' ? 0 : Math.max(0, Math.floor((Number(el.dataset.endAt) - now) / 1000));
      if (left <= 0 && !designMode) {
        wrap.hidden = true;
        return;
      }
      const pad = (n) => String(n).padStart(2, '0');
      const days = Math.floor(left / 86400);
      const hours = Math.floor((left % 86400) / 3600);
      setCountdownUnit(el, 'd', days, days > 0);
      setCountdownUnit(el, 'h', pad(hours), days > 0 || hours > 0);
      setCountdownUnit(el, 'm', pad(Math.floor((left % 3600) / 60)), true);
      setCountdownUnit(el, 's', pad(left % 60), true);
    });
  }
  let countdownInterval = null;
  function initCountdowns() {
    if (!$('[data-countdown]')) return;
    tickCountdowns();
    if (!countdownInterval) countdownInterval = setInterval(tickCountdowns, 1000);
  }

  /* ---------- Delivery estimate ---------- */
  function addBusinessDays(from, days) {
    const date = new Date(from);
    let added = 0;
    while (added < days) {
      date.setDate(date.getDate() + 1);
      const day = date.getDay();
      if (day !== 0 && day !== 6) added += 1;
    }
    return date;
  }
  function initDelivery(root) {
    $$('[data-delivery]', root).forEach((el) => {
      const min = Number(el.dataset.min) || 3;
      const max = Math.max(min, Number(el.dataset.max) || min);
      const format = new Intl.DateTimeFormat(document.documentElement.lang || undefined, { weekday: 'short', month: 'short', day: 'numeric' });
      const now = new Date();
      const range = min === max
        ? format.format(addBusinessDays(now, min))
        : `${format.format(addBusinessDays(now, min))} – ${format.format(addBusinessDays(now, max))}`;
      const target = $('[data-delivery-text]', el);
      const [before, after = ''] = (el.dataset.template || '[dates]').split('[dates]');
      target.textContent = '';
      target.append(before);
      const strong = document.createElement('strong');
      strong.textContent = range;
      target.append(strong, after);
    });
  }

  /* ---------- Announcement rotation ---------- */
  const rotators = new WeakSet();
  function initRotator(root) {
    $$('[data-rotator]', root).forEach((el) => {
      if (rotators.has(el)) return;
      rotators.add(el);
      const items = $$('.announcement__item', el);
      if (items.length < 2) return;
      const desktop = window.matchMedia('(min-width: 990px)');
      let i = 0;
      setInterval(() => {
        if (desktop.matches && items.length <= 3) return;
        items[i].classList.remove('is-active');
        i = (i + 1) % items.length;
        items[i].classList.add('is-active');
      }, (Number(el.dataset.interval) || 4) * 1000);
    });
  }

  /* ---------- Reveal on scroll ---------- */
  const revealObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -6% 0px', threshold: 0.06 })
    : null;
  function initReveal(root) {
    const items = $$('.reveal:not(.is-visible)', root);
    if (!revealObserver || reduceMotion || designMode || !document.body.classList.contains('has-animations')) {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    items.forEach((el) => revealObserver.observe(el));
  }

  /* ---------- How it works: highlight the ring for the step in view ---------- */
  function initSteps(root) {
    $$('[data-steps]', root).forEach((section) => {
      const steps = $$('[data-step]', section);
      if (!steps.length || !('IntersectionObserver' in window)) return;
      const activate = (step) => {
        section.dataset.active = step.dataset.step;
        steps.forEach((s) => s.classList.toggle('is-active', s === step));
      };
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => { if (entry.isIntersecting) activate(entry.target); });
      }, { rootMargin: '-45% 0px -45% 0px' });
      steps.forEach((step) => {
        observer.observe(step);
        step.addEventListener('mouseenter', () => activate(step));
      });
    });
  }

  /* ---------- Carousels ---------- */
  function initCarousels(root) {
    $$('[data-carousel]', root).forEach((carousel) => {
      const section = carousel.closest('section') || root;
      const track = $('[data-carousel-track]', carousel);
      const scrollBy = (dir) => {
        const card = track.firstElementChild;
        const width = card ? card.getBoundingClientRect().width + 16 : track.clientWidth * 0.8;
        track.scrollBy({ left: dir * width, behavior: reduceMotion ? 'auto' : 'smooth' });
      };
      const prev = $('[data-carousel-prev]', section);
      const next = $('[data-carousel-next]', section);
      if (prev) prev.addEventListener('click', () => scrollBy(-1));
      if (next) next.addEventListener('click', () => scrollBy(1));
    });
  }

  /* ---------- Product recommendations ---------- */
  function initRecommendations(root) {
    $$('[data-recommendations]', root).forEach(async (section) => {
      if (section.dataset.loaded || !section.dataset.url) return;
      section.dataset.loaded = 'true';
      try {
        const response = await fetch(section.dataset.url);
        const html = await response.text();
        const fresh = $('[data-recommendations]', new DOMParser().parseFromString(html, 'text/html'));
        if (fresh && fresh.innerHTML.trim()) {
          section.innerHTML = fresh.innerHTML;
          initReveal(section);
        }
      } catch (error) { /* recommendations are optional */ }
    });
  }

  /* ---------- Header scroll state ---------- */
  function initHeader() {
    const header = $('[data-header]');
    if (!header) return;
    const update = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
    update();
    window.addEventListener('scroll', update, { passive: true });
  }

  /* ---------- Boot ---------- */
  function init(root = document) {
    $$('[data-product]', root).forEach(initProduct);
    initDelivery(root);
    initCountdowns();
    initRotator(root);
    initReveal(root);
    initSteps(root);
    initCarousels(root);
    initRecommendations(root);
  }

  initHeader();
  init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:select', (event) => {
    if (event.target.querySelector('[data-cart-drawer]')) openDrawer();
  });
  document.addEventListener('shopify:block:select', (event) => {
    const item = event.target.closest('.announcement__item');
    if (!item) return;
    $$('.announcement__item', item.parentElement).forEach((el) => el.classList.toggle('is-active', el === item));
  });
})();
